import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  TrashIcon,
  BuildingOfficeIcon,
  PaperClipIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  CloudArrowUpIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  ShareIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { RichTextEditor, RichTextDisplay } from '@/components/ui/RichTextEditor';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { toDateTimeLocalValue } from '@/utils/datetime';
import LocationPicker from '@/components/maps/LocationPicker';
import GoogleMap from '@/components/maps/GoogleMap';
import CustomFieldsEditor, { type CustomField } from '@/components/executive-protection/CustomFieldsEditor';
import CommunicationPlanEditor, { type CommunicationPlan, type CommsChannel, validateCommunicationPlan } from '@/components/executive-protection/CommunicationPlanEditor';
import AfterActionReportForm, { type AarFormData } from '@/components/executive-protection/AfterActionReportForm';
import ShareOpsPlanModal from '@/components/executive-protection/ShareOpsPlanModal';
import SharesPanel from '@/components/executive-protection/SharesPanel';
import AgentMultiSelect, { type AgentSelection } from '@/components/executive-protection/AgentMultiSelect';
import { useAnyRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { cn } from '@/utils/cn';
import { executiveProtectionService, ActivityApiError, type Activity, type ActivityAttachment, type CreateCorrectiveActionData, type UpdateCorrectiveActionData } from '@/services/executive-protection.service';
import { apiClient } from '@/services/api';
import { DOCUMENT_FILE_EXTENSIONS } from '@/constants/file-upload';

interface EditFormData {
  title: string;
  description: string;
  type: 'meeting' | 'travel' | 'event' | 'other';
  location: string;
  locationLatitude?: number;
  locationLongitude?: number;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'debriefing' | 'closed';
  riskAssessment?: string;
  securityRequirements?: string;
  closestHospitalName?: string;
  closestHospitalAddress?: string;
  closestHospitalPhone?: string;
  closestHospitalDistance?: string;
  closestHospitalTravelTime?: string;
  closestHospitalCapabilities?: string;
  alternativeMedicalFacilities?: string;
  evacuationRoute?: string;
  onsiteMedicalSupport?: string;
  customFields?: CustomField[];
  communicationPlan?: CommunicationPlan | null;
}

// Helper to normalize status from backend to frontend type
const normalizeStatus = (status: string | undefined): EditFormData['status'] => {
  const normalized = status?.toLowerCase().replace('planned', 'scheduled');
  if (normalized === 'scheduled' || normalized === 'in_progress' || normalized === 'completed' || normalized === 'cancelled' || normalized === 'debriefing' || normalized === 'closed') {
    return normalized;
  }
  return 'scheduled';
};

// Helper to normalize type from backend to frontend type
const normalizeType = (type: string | undefined): EditFormData['type'] => {
  const normalized = type?.toLowerCase();
  if (normalized === 'meeting' || normalized === 'travel' || normalized === 'event') {
    return normalized;
  }
  return 'other';
};

export default function ActivityDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof EditFormData, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'aar' | 'attachments' | 'shares'>('details');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReopenDialogOpen, setIsReopenDialogOpen] = useState(false);
  const [reopenEnd, setReopenEnd] = useState('');
  const [reopenError, setReopenError] = useState<string | null>(null);
  const isManager = useAnyRole(['SUPER_ADMIN', 'ADMIN', 'SECURITY_MANAGER']);
  const [agentSelection, setAgentSelection] = useState<AgentSelection>({ agentIds: [], leadAgentId: null });
  const canUpdateActivities = usePermission('executive_protection:update');
  const canViewCommsPlan = useAnyRole(['SUPER_ADMIN', 'ADMIN', 'SECURITY_MANAGER', 'ANALYST', 'OPERATOR']);

  // Fetch activity data from API - use apiClient directly to distinguish 404 from server errors
  const { data: activity, isLoading, error } = useQuery({
    queryKey: ['executive-protection', 'activity', id],
    queryFn: async () => {
      if (!id) { return null; }
      const response = await apiClient.get<Activity>(`/api/v1/executive-protection/activities/${id}`);
      if (!response.success) {
        // Distinguish between "not found" (return null) and server errors (throw)
        const errorCode = (response.error as { code?: string })?.code;
        if (errorCode === 'NOT_FOUND') {
          return null;
        }
        const errorMessage = (response.error as { message?: string })?.message
          ?? (typeof response.error === 'string' ? response.error : 'Failed to load activity');
        throw new Error(errorMessage);
      }
      return response.data ?? null;
    },
    enabled: !!id,
    retry: 2,
  });

  // Fetch protected person data for this activity (backward compatibility)
  const { data: protectedPerson } = useQuery({
    queryKey: ['executive-protection', 'person', activity?.protectedPersonId],
    queryFn: async () => {
      if (!activity?.protectedPersonId) { return null; }
      return await executiveProtectionService.getProtectedPersonById(activity.protectedPersonId);
    },
    enabled: !!activity?.protectedPersonId,
  });

  // Get all protected persons for this activity (from protectedPersons array if available)
  const protectedPersons = activity?.protectedPersons ?? (protectedPerson ? [protectedPerson] : []);

  // Update mutation
  const updateActivityMutation = useMutation({
    mutationFn: (data: Partial<Activity>) => {
      if (!id) { throw new Error('No ID provided'); }
      return executiveProtectionService.updateActivity(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activities'] });
      setIsEditing(false);
    },
  });

  const startDebriefMutation = useMutation({
    mutationFn: () => executiveProtectionService.startDebrief(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
      setActiveTab('aar');
    },
  });

  const saveAarDraftMutation = useMutation({
    mutationFn: (data: AarFormData) => executiveProtectionService.saveAarDraft(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
    },
  });

  const completeAarMutation = useMutation({
    mutationFn: (data: AarFormData) => executiveProtectionService.completeAar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
    },
  });

  const revertDebriefMutation = useMutation({
    mutationFn: () => executiveProtectionService.revertDebrief(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
      setActiveTab('details');
    },
  });

  // Corrective actions query
  const showAarTab = activity?.status === 'DEBRIEFING' || activity?.status === 'CLOSED';
  const { data: correctiveActions = [] } = useQuery({
    queryKey: ['executive-protection', 'activity', id, 'corrective-actions'],
    queryFn: () => executiveProtectionService.getCorrectiveActions(id),
    enabled: !!id && showAarTab,
  });

  const createCorrectiveActionMutation = useMutation({
    mutationFn: (data: CreateCorrectiveActionData) => executiveProtectionService.createCorrectiveAction(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id, 'corrective-actions'] });
    },
  });

  const updateCorrectiveActionMutation = useMutation({
    mutationFn: ({ actionId, data }: { actionId: string; data: UpdateCorrectiveActionData }) =>
      executiveProtectionService.updateCorrectiveAction(id, actionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id, 'corrective-actions'] });
    },
  });

  const deleteCorrectiveActionMutation = useMutation({
    mutationFn: (actionId: string) => executiveProtectionService.deleteCorrectiveAction(id, actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id, 'corrective-actions'] });
    },
  });

  const [editData, setEditData] = useState<EditFormData>({
    title: activity?.title ?? '',
    description: activity?.description ?? '',
    type: normalizeType(activity?.activityType ?? activity?.type),
    location: activity?.location ?? '',
    startTime: activity?.startDatetime ?? activity?.startTime ?? '',
    endTime: activity?.endDatetime ?? activity?.endTime ?? '',
    status: normalizeStatus(activity?.status),
  });

  // Update editData when activity data loads
  useEffect(() => {
    if (activity && !isEditing) {
      setEditData({
        title: activity?.title ?? "",
        description: activity?.description ?? '',
        type: normalizeType(activity.activityType ?? activity.type),
        location: activity?.location ?? "",
        locationLatitude: activity?.locationLatitude ?? undefined,
        locationLongitude: activity?.locationLongitude ?? undefined,
        startTime: getStartTime() ?? '',
        endTime: getEndTime() ?? '',
        status: normalizeStatus(activity.status),
        riskAssessment: activity.riskAssessment ?? '',
        securityRequirements: activity.securityRequirements ?? '',
        closestHospitalName: activity.closestHospitalName ?? '',
        closestHospitalAddress: activity.closestHospitalAddress ?? '',
        closestHospitalPhone: activity.closestHospitalPhone ?? '',
        closestHospitalDistance: activity.closestHospitalDistance ?? '',
        closestHospitalTravelTime: activity.closestHospitalTravelTime ?? '',
        closestHospitalCapabilities: activity.closestHospitalCapabilities ?? '',
        alternativeMedicalFacilities: activity.alternativeMedicalFacilities ?? '',
        evacuationRoute: activity.evacuationRoute ?? '',
        onsiteMedicalSupport: activity.onsiteMedicalSupport ?? '',
        customFields: activity.customFields ?? [],
        communicationPlan: activity.communicationPlan ?? null,
      });
      setAgentSelection({
        agentIds: activity.agentIds ?? [],
        leadAgentId: activity.leadAgentId ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getStartTime/getEndTime are derived from activity which is already in deps
  }, [activity, isEditing]);

  // Auto-switch to AAR tab when activity is in DEBRIEFING or CLOSED state
  useEffect(() => {
    if (activity?.status === 'DEBRIEFING' || activity?.status === 'CLOSED') {
      setActiveTab('aar');
    }
  }, [activity?.status]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <p className="text-secondary-600 dark:text-secondary-400">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Failed to Load Activity
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] })}>
              Try Again
            </Button>
            <Button variant="outline" onClick={() => navigate('/security/executive-protection')}>
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Executive Protection
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Activity Not Found
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            The activity you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button onClick={() => navigate('/security/executive-protection')}>
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Executive Protection
          </Button>
        </div>
      </div>
    );
  }

  // Safe to access activity properties now that we've checked it exists
  const isCompleted = activity.status === 'COMPLETED';
  const isCancelled = activity.status === 'CANCELLED';
  const isDebriefing = activity.status === 'DEBRIEFING';
  const isClosed = activity.status === 'CLOSED';
  const isReadOnly = isCompleted || isCancelled || isDebriefing || isClosed;

  // Helper functions to handle both old and new field naming conventions
  const getActivityType = () => activity.activityType ?? activity.type;
  const getStartTime = () => activity.startDatetime ?? activity.startTime;
  const getEndTime = () => activity.endDatetime ?? activity.endTime;

  const statusColors = {
    scheduled: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    planned: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    in_progress: 'bg-warning-500 text-white dark:bg-warning-600 dark:text-white',
    completed: 'bg-success-500 text-white dark:bg-success-600 dark:text-white',
    cancelled: 'bg-secondary-400 text-white dark:bg-secondary-600 dark:text-white',
    debriefing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    closed: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  };

  const typeColors: Record<string, string> = {
    meeting: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    travel: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
    event: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    dining: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    other: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTimeForInput = (timestamp: string) => {
    if (!timestamp) { return ''; }
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) { return ''; }
    return toDateTimeLocalValue(date); // Local wall time, not UTC
  };

  const renderCommsChannel = (label: string, channel: CommsChannel) => (
    <div className="pb-4 border-b border-secondary-200 dark:border-secondary-700 last:border-0 last:pb-0">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="font-semibold text-secondary-900 dark:text-white">{label}</h4>
        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded">
          {channel.method}
        </span>
        {channel.channelDetail && (
          <span className="text-sm text-secondary-600 dark:text-secondary-400">{channel.channelDetail}</span>
        )}
      </div>
      <div className="space-y-1">
        {channel.contacts.map((contact, index) => (
          <div key={index} className="text-sm">
            <span className="font-medium text-secondary-900 dark:text-white">{contact.name}</span>
            <span className="text-secondary-500 dark:text-secondary-400 mx-2">—</span>
            {channel.method === 'PHONE' ? (
              <a href={`tel:${contact.contact}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                {contact.contact}
              </a>
            ) : (
              <span className="text-secondary-700 dark:text-secondary-300">{contact.contact}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const handleEdit = () => {
    setEditData({
      title: activity?.title ?? "",
      description: activity?.description ?? '',
      type: normalizeType(getActivityType()),
      location: activity?.location ?? "",
      locationLatitude: activity?.locationLatitude ?? undefined,
      locationLongitude: activity?.locationLongitude ?? undefined,
      startTime: getStartTime() ?? '',
      endTime: getEndTime() ?? '',
      status: normalizeStatus(activity.status),
      riskAssessment: activity.riskAssessment ?? '',
      securityRequirements: activity.securityRequirements ?? '',
      closestHospitalName: activity.closestHospitalName ?? '',
      closestHospitalAddress: activity.closestHospitalAddress ?? '',
      closestHospitalPhone: activity.closestHospitalPhone ?? '',
      closestHospitalDistance: activity.closestHospitalDistance ?? '',
      closestHospitalTravelTime: activity.closestHospitalTravelTime ?? '',
      closestHospitalCapabilities: activity.closestHospitalCapabilities ?? '',
      alternativeMedicalFacilities: activity.alternativeMedicalFacilities ?? '',
      evacuationRoute: activity.evacuationRoute ?? '',
      onsiteMedicalSupport: activity.onsiteMedicalSupport ?? '',
      customFields: activity.customFields ?? [],
      communicationPlan: activity.communicationPlan ?? null,
    });
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditData({
      title: activity?.title ?? "",
      description: activity?.description ?? '',
      type: normalizeType(activity.type),
      location: activity?.location ?? "",
      locationLatitude: activity?.locationLatitude ?? undefined,
      locationLongitude: activity?.locationLongitude ?? undefined,
      startTime: getStartTime() ?? '',
      endTime: getEndTime() ?? '',
      status: normalizeStatus(activity.status),
      riskAssessment: activity.riskAssessment ?? '',
      securityRequirements: activity.securityRequirements ?? '',
      closestHospitalName: activity.closestHospitalName ?? '',
      closestHospitalAddress: activity.closestHospitalAddress ?? '',
      closestHospitalPhone: activity.closestHospitalPhone ?? '',
      closestHospitalDistance: activity.closestHospitalDistance ?? '',
      closestHospitalTravelTime: activity.closestHospitalTravelTime ?? '',
      closestHospitalCapabilities: activity.closestHospitalCapabilities ?? '',
      alternativeMedicalFacilities: activity.alternativeMedicalFacilities ?? '',
      evacuationRoute: activity.evacuationRoute ?? '',
      onsiteMedicalSupport: activity.onsiteMedicalSupport ?? '',
      customFields: activity.customFields ?? [],
      communicationPlan: activity.communicationPlan ?? null,
    });
    setErrors({});
    setSaveError(null);
    setIsEditing(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof EditFormData, string>> = {};

    if (!editData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    // Location is optional on create (ActivityForm + backend schema); keep edit consistent.
    if (!editData.startTime) {
      newErrors.startTime = 'Start time is required';
    }
    if (!editData.endTime) {
      newErrors.endTime = 'End time is required';
    }
    if (editData.startTime && editData.endTime) {
      const start = new Date(editData.startTime);
      const end = new Date(editData.endTime);
      if (end <= start) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setSaveError(null);
    if (!validateForm()) {
      return;
    }

    if (editData.communicationPlan) {
      const planError = validateCommunicationPlan(editData.communicationPlan);
      if (planError) {
        setErrors((prev) => ({ ...prev, communicationPlan: planError } as typeof prev));
        return;
      }
    }

    const statusToBackend: Record<string, string> = {
      scheduled: 'PLANNED',
      in_progress: 'IN_PROGRESS',
      completed: 'COMPLETED',
      cancelled: 'CANCELLED',
    };

    const typeToBackend: Record<string, 'TRAVEL' | 'DINING' | 'EVENT' | 'MEETING' | 'OTHER'> = {
      meeting: 'MEETING',
      travel: 'TRAVEL',
      event: 'EVENT',
      other: 'OTHER',
    };

    try {
      await updateActivityMutation.mutateAsync({
        title: editData.title,
        description: editData.description,
        activityType: typeToBackend[editData.type] ?? 'OTHER',
        location: editData.location,
        locationLatitude: editData.locationLatitude,
        locationLongitude: editData.locationLongitude,
        startDatetime: editData.startTime,
        endDatetime: editData.endTime,
        status: (statusToBackend[editData.status] ?? editData.status.toUpperCase()) as 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED',
        riskAssessment: editData.riskAssessment,
        securityRequirements: editData.securityRequirements,
        // Medical information fields
        closestHospitalName: editData.closestHospitalName,
        closestHospitalAddress: editData.closestHospitalAddress,
        closestHospitalPhone: editData.closestHospitalPhone,
        closestHospitalDistance: editData.closestHospitalDistance,
        closestHospitalTravelTime: editData.closestHospitalTravelTime,
        closestHospitalCapabilities: editData.closestHospitalCapabilities,
        alternativeMedicalFacilities: editData.alternativeMedicalFacilities,
        evacuationRoute: editData.evacuationRoute,
        onsiteMedicalSupport: editData.onsiteMedicalSupport,
        // Custom fields (Additional Information)
        customFields: editData.customFields,
        // Security detail (assigned agents)
        agentIds: agentSelection.agentIds,
        leadAgentId: agentSelection.leadAgentId ?? undefined,
        communicationPlan: editData.communicationPlan,
      });
    } catch (error) {
      console.error('Failed to update activity:', error);
      if (error instanceof ActivityApiError && error.code === 'END_IN_PAST') {
        setErrors(prev => ({ ...prev, endTime: error.message }));
        setSaveError(error.message);
      } else if (error instanceof Error) {
        setSaveError(error.message);
      } else {
        setSaveError('Failed to save changes. Please try again.');
      }
    }
  };

  const handleChange = (field: keyof EditFormData, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Reopen an auto-completed activity with a corrected end time (VIS-136)
  const handleReopen = async () => {
    setReopenError(null);
    if (!reopenEnd) {
      setReopenError('A new end date/time is required to reopen this activity.');
      return;
    }
    const startTime = getStartTime();
    const nextStatus = startTime && new Date(startTime) < new Date() ? 'IN_PROGRESS' : 'PLANNED';
    try {
      await updateActivityMutation.mutateAsync({
        status: nextStatus,
        endDatetime: new Date(reopenEnd).toISOString(),
      });
      setIsReopenDialogOpen(false);
      setReopenEnd('');
    } catch (error) {
      setReopenError(error instanceof Error ? error.message : 'Failed to reopen activity');
    }
  };

  const handleDelete = async () => {
    if (!id) { return; }

    setIsDeleting(true);
    try {
      await executiveProtectionService.deleteActivity(id);
      // Invalidate queries and navigate back to list
      queryClient.invalidateQueries({ queryKey: ['executive-protection'] });
      navigate('/security/executive-protection');
    } catch (error) {
      console.error('Failed to delete activity:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) { return; }

    setIsUploading(true);
    try {
      const result = await executiveProtectionService.uploadActivityAttachment(id, file);
      if (result) {
        // Only refresh activity data if upload was successful
        queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
      } else {
        // Upload returned null (server error) - don't invalidate queries to avoid cascading failure
        // eslint-disable-next-line no-alert
        alert('Failed to upload file. The server encountered an error. Please try again.');
      }
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      // eslint-disable-next-line no-alert
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = async (attachment: ActivityAttachment) => {
    try {
      await executiveProtectionService.downloadActivityAttachment(
        attachment.id,
        attachment.originalFilename
      );
    } catch (error) {
      console.error('Failed to download attachment:', error);
      // eslint-disable-next-line no-alert
      alert('Failed to download attachment. Please try again.');
    }
  };

  const handleDeleteAttachment = async () => {
    if (!attachmentToDelete) { return; }

    try {
      await executiveProtectionService.deleteActivityAttachment(attachmentToDelete);
      // Refresh activity data to get updated attachments
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activity', id] });
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      // eslint-disable-next-line no-alert
      alert('Failed to delete attachment. Please try again.');
    } finally {
      setAttachmentToDelete(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) { return '0 Bytes'; }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) { return '🖼️'; }
    if (mimeType.includes('pdf')) { return '📄'; }
    if (mimeType.includes('word') || mimeType.includes('document')) { return '📝'; }
    if (mimeType.includes('sheet') || mimeType.includes('excel')) { return '📊'; }
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) { return '📊'; }
    return '📎';
  };

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasMedicalInfo = Boolean(activity.closestHospitalName || activity.closestHospitalAddress || activity.alternativeMedicalFacilities || activity.evacuationRoute || activity.onsiteMedicalSupport || isEditing);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasClosestHospital = Boolean(activity.closestHospitalName || activity.closestHospitalAddress || activity.closestHospitalPhone || activity.closestHospitalDistance || activity.closestHospitalTravelTime || activity.closestHospitalCapabilities);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Link
              to="/security/executive-protection/activities"
              className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
                {activity.title}
              </h1>
              <p className="text-secondary-600 dark:text-secondary-400 mt-1">
                Activity Details
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            {!isEditing ? (
              <>
                {isCompleted && isManager && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const defaultEnd = new Date();
                      defaultEnd.setHours(defaultEnd.getHours() + 2);
                      setReopenEnd(toDateTimeLocalValue(defaultEnd));
                      setReopenError(null);
                      setIsReopenDialogOpen(true);
                    }}
                  >
                    <ArrowPathIcon className="w-4 h-4 mr-2" />
                    Reopen
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isReadOnly}
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={handleEdit} disabled={isReadOnly}>
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {isManager && (
                  <Button variant="outline" onClick={() => setIsShareModalOpen(true)}>
                    <ShareIcon className="mr-1.5 h-4 w-4" /> Share Ops Plan
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateActivityMutation.isPending}>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {updateActivityMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Save failure banner — keep next to the Save button so a failed save is visible */}
      {isEditing && saveError && (
        <motion.div variants={itemVariants}>
          <div
            role="alert"
            data-testid="save-error-banner"
            className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-lg"
          >
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-300">Save failed</p>
                <p className="text-sm text-red-700 dark:text-red-400">{saveError}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Status Badges */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-wrap gap-2">
          {getActivityType() && (
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full', typeColors[getActivityType()?.toLowerCase() ?? 'other'])}>
              {getActivityType()?.toUpperCase()}
            </span>
          )}
          <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[activity.status.toLowerCase() as keyof typeof statusColors] ?? statusColors.scheduled)}>
            {activity.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </motion.div>

      {/* Status Banners */}
      {isCompleted && (
        <motion.div variants={itemVariants}>
          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-300">
                    Activity Completed
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    This activity has been completed and can no longer be edited.
                  </p>
                </div>
              </div>
              {isManager && (
                <Button
                  onClick={() => startDebriefMutation.mutate()}
                  disabled={startDebriefMutation.isPending}
                >
                  <ClipboardDocumentListIcon className="w-4 h-4 mr-2" />
                  {startDebriefMutation.isPending ? 'Starting...' : 'Start Debrief'}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {isDebriefing && (
        <motion.div variants={itemVariants}>
          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <ClipboardDocumentListIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    After Action Report In Progress
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                    Complete the After Action Report to close this activity.
                  </p>
                </div>
              </div>
              {isManager && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => revertDebriefMutation.mutate()}
                  disabled={revertDebriefMutation.isPending}
                >
                  {revertDebriefMutation.isPending ? 'Reverting...' : 'Revert to Completed'}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {isClosed && (
        <motion.div variants={itemVariants}>
          <div className="bg-secondary-50 dark:bg-secondary-800/50 border-l-4 border-secondary-400 p-4 rounded-lg">
            <div className="flex items-start">
              <CheckIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Activity Closed
                </h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                  This activity and its After Action Report have been finalized.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {isCancelled && (
        <motion.div variants={itemVariants}>
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex items-start">
              <XMarkIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                  Activity Cancelled
                </h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  This activity has been cancelled and can no longer be edited.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tab Navigation */}
      <motion.div variants={itemVariants}>
        <div className="flex border-b border-secondary-200 dark:border-secondary-700">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-secondary-400'
            )}
          >
            Activity Details
          </button>
          {showAarTab && (
            <button
              onClick={() => setActiveTab('aar')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === 'aar'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-secondary-400'
              )}
            >
              After Action Report
            </button>
          )}
          <button
            onClick={() => setActiveTab('attachments')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'attachments'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-secondary-400'
            )}
          >
            Attachments
          </button>
          {isManager && (
            <button
              onClick={() => setActiveTab('shares')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === 'shares'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-secondary-400'
              )}
            >
              Sharing
            </button>
          )}
        </div>
      </motion.div>

      {activeTab === 'details' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Time & Location */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Time & Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isEditing ? (
                  <>
                    <div className="flex items-center space-x-3">
                      <CalendarIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                      <div>
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">Date</div>
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {getStartTime() ? formatDate(getStartTime() as string) : 'Not specified'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <ClockIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                      <div>
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">Time</div>
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {getStartTime() && getEndTime()
                            ? `${formatTime(getStartTime() as string)} - ${formatTime(getEndTime() as string)}`
                            : 'Not specified'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPinIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                      <div>
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">Location</div>
                        <div className="font-medium text-secondary-900 dark:text-white">{activity.location ?? 'Not specified'}</div>
                      </div>
                    </div>
                    {/* Mini-map preview when coordinates exist */}
                    {activity.locationLatitude && activity.locationLongitude && import.meta.env.VITE_GOOGLE_MAPS_API_KEY && (
                      <div className="col-span-full mt-4">
                        <div className="h-48 rounded-lg overflow-hidden border border-secondary-200 dark:border-secondary-700">
                          <GoogleMap
                            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                            center={{
                              lat: activity.locationLatitude,
                              lng: activity.locationLongitude
                            }}
                            zoom={15}
                            className="w-full h-full"
                            onMapLoad={(map) => {
                              // Create marker after map loads
                              const createMarker = async () => {
                                const { AdvancedMarkerElement, PinElement } =
                                  (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
                                const pinElement = new PinElement({
                                  background: '#EF4444',
                                  borderColor: '#FFFFFF',
                                  glyphColor: '#FFFFFF',
                                });
                                new AdvancedMarkerElement({
                                  map,
                                  position: {
                                    lat: activity.locationLatitude as number,
                                    lng: activity.locationLongitude as number
                                  },
                                  title: activity.location ?? 'Activity Location',
                                  content: pinElement.element,
                                });
                              };
                              void createMarker();
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <DateTimeInput
                        label="Start Time"
                        required
                        value={formatDateTimeForInput(editData.startTime)}
                        onChange={(value) => handleChange('startTime', value ? new Date(value).toISOString() : '')}
                        error={errors.startTime}
                      />
                    </div>
                    <div>
                      <DateTimeInput
                        label="End Time"
                        required
                        value={formatDateTimeForInput(editData.endTime)}
                        onChange={(value) => handleChange('endTime', value ? new Date(value).toISOString() : '')}
                        error={errors.endTime}
                      />
                    </div>
                    <div className="col-span-full">
                      <LocationPicker
                        apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''}
                        label="Location"
                        value={{
                          location: editData.location,
                          latitude: editData.locationLatitude,
                          longitude: editData.locationLongitude,
                        }}
                        onChange={({ location, latitude, longitude }) => {
                          setEditData(prev => ({
                            ...prev,
                            location,
                            locationLatitude: latitude,
                            locationLongitude: longitude,
                          }));
                        }}
                        placeholder="Search for a location or click on the map"
                        mapHeight="200px"
                        error={errors.location}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Description Section */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
                <CardDescription>Activity details and notes</CardDescription>
              </CardHeader>
              <CardContent>
                {!isEditing ? (
                  <RichTextDisplay
                    content={activity.description ?? ''}
                    className="text-secondary-700 dark:text-secondary-300"
                    emptyMessage="No description available."
                  />
                ) : (
                  <RichTextEditor
                    value={editData.description}
                    onChange={(html) => handleChange('description', html)}
                    placeholder="Add activity description or notes..."
                    minHeight={150}
                  />
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Risk Assessment Section */}
          {(activity.riskAssessment ?? isEditing) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle>Risk Assessment</CardTitle>
                  <CardDescription>Security risk evaluation</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isEditing ? (
                    <RichTextDisplay
                      content={activity.riskAssessment ?? ''}
                      className="text-secondary-700 dark:text-secondary-300"
                      emptyMessage="No risk assessment provided."
                    />
                  ) : (
                    <RichTextEditor
                      value={editData.riskAssessment ?? ''}
                      onChange={(html) => handleChange('riskAssessment', html)}
                      placeholder="Add risk assessment..."
                      minHeight={120}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Security Requirements Section */}
          {(activity.securityRequirements ?? isEditing) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle>Security Requirements</CardTitle>
                  <CardDescription>Required security measures</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isEditing ? (
                    <RichTextDisplay
                      content={activity.securityRequirements ?? ''}
                      className="text-secondary-700 dark:text-secondary-300"
                      emptyMessage="No security requirements specified."
                    />
                  ) : (
                    <RichTextEditor
                      value={editData.securityRequirements ?? ''}
                      onChange={(html) => handleChange('securityRequirements', html)}
                      placeholder="Add security requirements..."
                      minHeight={120}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Communication Plan Section */}
          {canViewCommsPlan && (activity.communicationPlan ?? isEditing) && (
            <motion.div variants={itemVariants}>
              <Card data-testid="communication-plan-card">
                <CardHeader>
                  <CardTitle>Communication Plan</CardTitle>
                  <CardDescription>Comms channels, agent contacts, and code words for this activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isEditing ? (
                    activity.communicationPlan && (
                      <div className="space-y-4">
                        {renderCommsChannel('Primary Comms', activity.communicationPlan.primary)}
                        {activity.communicationPlan.secondary &&
                          renderCommsChannel('Secondary Comms', activity.communicationPlan.secondary)}

                        {activity.communicationPlan.codeWords.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-secondary-900 dark:text-white mb-2">Code Words</h4>
                            <table className="w-full text-sm border border-amber-200 dark:border-amber-800 rounded" data-testid="code-words-table">
                              <tbody>
                                {activity.communicationPlan.codeWords.map((codeWord, index) => (
                                  <tr key={index} className="border-b border-amber-100 dark:border-amber-900 last:border-0 bg-amber-50 dark:bg-amber-900/10">
                                    <td className="px-3 py-2 font-bold text-secondary-900 dark:text-white w-1/3">{codeWord.word}</td>
                                    <td className="px-3 py-2 text-secondary-700 dark:text-secondary-300">{codeWord.meaning}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {activity.communicationPlan.notes && (
                          <div>
                            <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Notes</div>
                            <div className="text-secondary-900 dark:text-white whitespace-pre-wrap">{activity.communicationPlan.notes}</div>
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <CommunicationPlanEditor
                      value={editData.communicationPlan ?? null}
                      onChange={(plan) => setEditData((prev) => ({ ...prev, communicationPlan: plan }))}
                      error={errors.communicationPlan ?? null}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Medical Information Section */}
          {hasMedicalInfo && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HeartIcon className="w-5 h-5 mr-2 text-red-500" />
                    Medical Information
                  </CardTitle>
                  <CardDescription>Emergency medical planning and resources</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isEditing ? (
                    <div className="space-y-6">
                      {/* Closest Hospital */}
                      {hasClosestHospital && (
                        <div className="pb-4 border-b border-secondary-200 dark:border-secondary-700">
                          <h4 className="font-semibold text-secondary-900 dark:text-white mb-3">Closest Hospital</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {activity.closestHospitalName && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Hospital Name</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalName}</div>
                              </div>
                            )}
                            {activity.closestHospitalPhone && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Emergency Contact</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalPhone}</div>
                              </div>
                            )}
                            {activity.closestHospitalAddress && (
                              <div className="md:col-span-2">
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Address</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalAddress}</div>
                              </div>
                            )}
                            {activity.closestHospitalDistance && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Distance</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalDistance}</div>
                              </div>
                            )}
                            {activity.closestHospitalTravelTime && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Travel Time</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalTravelTime}</div>
                              </div>
                            )}
                            {activity.closestHospitalCapabilities && (
                              <div className="md:col-span-2">
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">Capabilities</div>
                                <div className="font-medium text-secondary-900 dark:text-white">{activity.closestHospitalCapabilities}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Emergency Planning */}
                      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                      {(activity.alternativeMedicalFacilities || activity.evacuationRoute || activity.onsiteMedicalSupport) && (
                        <div>
                          <h4 className="font-semibold text-secondary-900 dark:text-white mb-3">Emergency Planning</h4>
                          <div className="space-y-4">
                            {activity.alternativeMedicalFacilities && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Alternative Medical Facilities</div>
                                <div className="text-secondary-900 dark:text-white whitespace-pre-wrap">{activity.alternativeMedicalFacilities}</div>
                              </div>
                            )}
                            {activity.evacuationRoute && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Evacuation Route</div>
                                <div className="text-secondary-900 dark:text-white whitespace-pre-wrap">{activity.evacuationRoute}</div>
                              </div>
                            )}
                            {activity.onsiteMedicalSupport && (
                              <div>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">On-Site Medical Support</div>
                                <div className="text-secondary-900 dark:text-white whitespace-pre-wrap">{activity.onsiteMedicalSupport}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show placeholder when in edit mode but no data */}
                      {!activity.closestHospitalName && !activity.closestHospitalAddress && !activity.alternativeMedicalFacilities &&
                        !activity.evacuationRoute && !activity.onsiteMedicalSupport && isEditing && (
                          <p className="text-secondary-500 dark:text-secondary-400 italic">No medical information provided</p>
                        )}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Edit Mode - Closest Hospital */}
                      <div className="pb-4 border-b border-secondary-200 dark:border-secondary-700">
                        <h4 className="font-semibold text-secondary-900 dark:text-white mb-3">Closest Hospital</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Hospital Name
                            </label>
                            <input
                              type="text"
                              value={editData.closestHospitalName ?? ''}
                              onChange={(e) => handleChange('closestHospitalName', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., City General Hospital"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Hospital Phone
                            </label>
                            <input
                              type="tel"
                              value={editData.closestHospitalPhone ?? ''}
                              onChange={(e) => handleChange('closestHospitalPhone', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., (555) 123-4567"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Hospital Address
                            </label>
                            <input
                              type="text"
                              value={editData.closestHospitalAddress ?? ''}
                              onChange={(e) => handleChange('closestHospitalAddress', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., 123 Main Street, City, State 12345"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Distance from Location
                            </label>
                            <input
                              type="text"
                              value={editData.closestHospitalDistance ?? ''}
                              onChange={(e) => handleChange('closestHospitalDistance', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., 2.5 miles"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Travel Time
                            </label>
                            <input
                              type="text"
                              value={editData.closestHospitalTravelTime ?? ''}
                              onChange={(e) => handleChange('closestHospitalTravelTime', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., 8 minutes"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Hospital Capabilities
                            </label>
                            <input
                              type="text"
                              value={editData.closestHospitalCapabilities ?? ''}
                              onChange={(e) => handleChange('closestHospitalCapabilities', e.target.value)}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="e.g., Level I Trauma Center, 24/7 Emergency Room"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Edit Mode - Emergency Planning */}
                      <div>
                        <h4 className="font-semibold text-secondary-900 dark:text-white mb-3">Emergency Planning</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Alternative Medical Facilities
                            </label>
                            <textarea
                              value={editData.alternativeMedicalFacilities ?? ''}
                              onChange={(e) => handleChange('alternativeMedicalFacilities', e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="List backup hospitals, urgent care centers, or clinics nearby"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Evacuation Route
                            </label>
                            <textarea
                              value={editData.evacuationRoute ?? ''}
                              onChange={(e) => handleChange('evacuationRoute', e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="Describe the planned route to the hospital in case of emergency"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              On-Site Medical Support
                            </label>
                            <textarea
                              value={editData.onsiteMedicalSupport ?? ''}
                              onChange={(e) => handleChange('onsiteMedicalSupport', e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                              placeholder="Describe any medical personnel, equipment, or resources available at the location"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Custom Fields Section (Additional Information) */}
          {(isEditing || (activity.customFields && activity.customFields.length > 0)) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                  <CardDescription>Activity-specific details</CardDescription>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    // Edit mode - full CustomFieldsEditor with add/remove/edit capabilities
                    <CustomFieldsEditor
                      activityType={editData.type?.toUpperCase() ?? 'OTHER'}
                      customFields={editData.customFields ?? []}
                      onChange={(fields) => setEditData(prev => ({ ...prev, customFields: fields }))}
                    />
                  ) : (
                    // View mode - read-only display
                    <CustomFieldsEditor
                      activityType={activity.type?.toUpperCase() ?? activity.activityType ?? 'OTHER'}
                      customFields={activity.customFields ?? []}
                      onChange={() => { }}
                      readOnly
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

        </div>

        {/* Sidebar Column */}
        <div className="space-y-6">
          {/* Activity Information */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Activity Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isEditing ? (
                  <>
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Title</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{activity.title}</div>
                    </div>
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Type</div>
                      <div className="font-medium text-secondary-900 dark:text-white capitalize">{getActivityType() ?? 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Status</div>
                      <div className="font-medium text-secondary-900 dark:text-white capitalize">
                        {activity.status.replace('_', ' ')}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        className={cn(
                          'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.title ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                          'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                        )}
                      />
                      {errors.title && <p className="mt-1 text-sm text-danger-600">{errors.title}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Type
                      </label>
                      <select
                        value={editData.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      >
                        <option value="meeting">Meeting</option>
                        <option value="travel">Travel</option>
                        <option value="event">Event</option>
                        <option value="dining">Dining</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Status
                      </label>
                      <select
                        value={editData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Protected Persons */}
          {protectedPersons.length > 0 && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle>
                    Protected Person{protectedPersons.length > 1 ? 's' : ''}
                    {protectedPersons.length > 1 && (
                      <span className="ml-2 text-sm font-normal text-secondary-600 dark:text-secondary-400">
                        ({protectedPersons.length} persons)
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {protectedPersons.map((person, index) => (
                    <div key={person.id} className={cn(
                      "p-4 rounded-lg border",
                      index === 0
                        ? "border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10"
                        : "border-secondary-200 dark:border-secondary-700"
                    )}>
                      {index === 0 && (
                        <div className="mb-2">
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded">
                            PRIMARY
                          </span>
                        </div>
                      )}
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                          <div>
                            <div className="text-sm text-secondary-600 dark:text-secondary-400">Name</div>
                            <Link
                              to={`/security/executive-protection/${person.id}`}
                              className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {person.firstName} {person.lastName}
                            </Link>
                          </div>
                        </div>
                        {person.title && (
                          <div className="flex items-center space-x-3">
                            <BuildingOfficeIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                            <div>
                              <div className="text-sm text-secondary-600 dark:text-secondary-400">Title</div>
                              <div className="font-medium text-secondary-900 dark:text-white">{person.title}</div>
                            </div>
                          </div>
                        )}
                        {person.riskLevel && (
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "px-2 py-1 text-xs font-medium rounded",
                              person.riskLevel === 'LOW' && "bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300",
                              person.riskLevel === 'MEDIUM' && "bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300",
                              person.riskLevel === 'HIGH' && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300",
                              person.riskLevel === 'CRITICAL' && "bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300"
                            )}>
                              {person.riskLevel} RISK
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Security Detail (assigned agents) */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 mr-2" />
                  Security Detail
                  {!isEditing && (activity.agents?.length ?? 0) > 0 && (
                    <span className="ml-2 text-sm font-normal text-secondary-600 dark:text-secondary-400">
                      ({activity.agents?.length} agent{(activity.agents?.length ?? 0) > 1 ? 's' : ''})
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isEditing ? (
                  <AgentMultiSelect
                    value={agentSelection}
                    onChange={setAgentSelection}
                    startDatetime={editData.startTime}
                    endDatetime={editData.endTime}
                    excludeActivityId={id}
                  />
                ) : (activity.agents?.length ?? 0) === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-secondary-600 dark:text-secondary-400 mb-3">No agents assigned</p>
                    {canUpdateActivities && !isReadOnly && (
                      <Button onClick={handleEdit}>
                        <ShieldCheckIcon className="w-4 h-4 mr-2" />
                        Assign Agents
                      </Button>
                    )}
                  </div>
                ) : (
                  activity.agents?.map((agent) => (
                    <div
                      key={agent.userId}
                      className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700"
                    >
                      <div className="flex items-center space-x-3">
                        <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                        <div>
                          <div className="font-medium text-secondary-900 dark:text-white">
                            {agent.name ?? 'Unknown user'}
                          </div>
                          {agent.role && (
                            <div className="text-sm text-secondary-600 dark:text-secondary-400">{agent.role}</div>
                          )}
                        </div>
                      </div>
                      {agent.isLead && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded">
                          TEAM LEAD
                        </span>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
      )}

      {activeTab === 'aar' && showAarTab && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>After Action Report</CardTitle>
              <CardDescription>
                {isDebriefing
                  ? 'Complete the debrief for this activity.'
                  : 'Finalized After Action Report.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AfterActionReportForm
                aarSummary={activity.aarSummary}
                aarTimeline={activity.aarTimeline}
                aarRecommendations={activity.aarRecommendations}
                aarPositives={activity.aarPositives}
                aarNegatives={activity.aarNegatives}
                aarPreparedByName={activity.aarPreparedByName}
                aarPreparedByTitle={activity.aarPreparedByTitle}
                aarPreparedAt={activity.aarPreparedAt}
                aarApprovedByName={activity.aarApprovedByName}
                aarApprovedByTitle={activity.aarApprovedByTitle}
                aarApprovedAt={activity.aarApprovedAt}
                aarCompletedAt={activity.aarCompletedAt}
                aarCompletedBy={activity.aarCompletedBy}
                correctiveActions={correctiveActions}
                onCreateCorrectiveAction={async (data) => {
                  await createCorrectiveActionMutation.mutateAsync(data);
                }}
                onUpdateCorrectiveAction={async (actionId, data) => {
                  await updateCorrectiveActionMutation.mutateAsync({ actionId, data });
                }}
                onDeleteCorrectiveAction={async (actionId) => {
                  await deleteCorrectiveActionMutation.mutateAsync(actionId);
                }}
                hasLegacyData={[activity.aarSummary, activity.aarTimeline, activity.aarRecommendations].some(Boolean)}
                readOnly={!isDebriefing || !isManager}
                onSaveDraft={async (data) => {
                  await saveAarDraftMutation.mutateAsync(data);
                }}
                onComplete={async (data) => {
                  await completeAarMutation.mutateAsync(data);
                }}
                isSaving={saveAarDraftMutation.isPending || completeAarMutation.isPending}
              />
            </CardContent>
          </Card>
        </motion.div>
      )}

      {activeTab === 'attachments' && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <PaperClipIcon className="w-5 h-5 mr-2" />
                    Attachments
                  </CardTitle>
                  <CardDescription>
                    Documents, itineraries, and other activity-related files
                  </CardDescription>
                </div>
                {!(isReadOnly && !isDebriefing) && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept={DOCUMENT_FILE_EXTENSIONS}
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      size="sm"
                    >
                      <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                      {isUploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {activity.attachments && activity.attachments.length > 0 ? (
                <div className="space-y-3">
                  {activity.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="text-2xl flex-shrink-0">
                          {getFileIcon(attachment.mimeType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-secondary-900 dark:text-white truncate">
                            {attachment.originalFilename}
                          </div>
                          <div className="text-sm text-secondary-600 dark:text-secondary-400">
                            {formatFileSize(attachment.fileSize)} • {new Date(attachment.uploadedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </Button>
                        {!(isReadOnly && !isDebriefing) && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setAttachmentToDelete(attachment.id)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                  <DocumentIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No attachments yet</p>
                  {!(isReadOnly && !isDebriefing) && (
                    <p className="text-sm mt-1">Upload documents, itineraries, or other files</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {activeTab === 'shares' && isManager && (
        <SharesPanel activityId={id} />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Activity"
        message={`Are you sure you want to delete "${activity.title}"? This action cannot be undone.`}
        confirmText="Delete Activity"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Delete Attachment Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(null)}
        onConfirm={handleDeleteAttachment}
        title="Delete Attachment"
        message="Are you sure you want to delete this attachment? This action cannot be undone."
        confirmText="Delete Attachment"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Reopen Activity Dialog (VIS-136): recover an auto-completed op */}
      {isReopenDialogOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setIsReopenDialogOpen(false)}
          />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-md w-full pointer-events-auto p-6 space-y-4">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Reopen Activity
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                This activity was marked completed because its end time passed. To reopen it
                for editing and document uploads, set a new end date/time in the future.
              </p>
              <DateTimeInput
                label="New End Date & Time"
                required
                value={reopenEnd}
                onChange={setReopenEnd}
                error={reopenError ?? undefined}
              />
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsReopenDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleReopen} disabled={updateActivityMutation.isPending}>
                  {updateActivityMutation.isPending ? 'Reopening...' : 'Reopen Activity'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Share Ops Plan Modal */}
      {isManager && activity && (
        <ShareOpsPlanModal
          activityId={id}
          activityEnd={activity.endDatetime ?? null}
          attachments={(activity.attachments ?? []).map((a) => ({ id: a.id, originalFilename: a.originalFilename }))}
          principalEmails={protectedPersons
            .filter((p): p is typeof p & { email: string } => !!p.email)
            .map((p) => ({ name: `${p.firstName} ${p.lastName}`, email: p.email }))}
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
    </motion.div>
  );
}
